/**
 * OAuth Token Exchange Utilities
 *
 * Handles the exchange of OAuth authorization codes for access tokens,
 * including PKCE verification and manual token management for Keycloak.
 */

const KEYCLOAK_URL = import.meta.env.VITE_KEYCLOAK_URL;
const KEYCLOAK_REALM = import.meta.env.VITE_KEYCLOAK_REALM;
const TOKEN_URL = `${KEYCLOAK_URL}/realms/${KEYCLOAK_REALM}/protocol/openid-connect/token`;

interface TokenExchangeParams {
  code: string;
  codeVerifier: string;
}

interface TokenResponse {
  access_token?: string;
  refresh_token?: string;
  id_token?: string;
  error?: string;
  error_description?: string;
}

/**
 * Exchanges OAuth authorization code for access tokens
 *
 * Performs the OAuth2 token exchange with PKCE verification. This bypasses
 * Keycloak's built-in token handling to work around Capacitor/Cordova limitations.
 *
 * @param params - Token exchange parameters including auth code and PKCE verifier
 * @returns Promise resolving to token response from Keycloak server
 */
export const exchangeCodeForTokens = async (params: TokenExchangeParams): Promise<TokenResponse> => {
  const { code, codeVerifier } = params;

  // Prepare token exchange payload according to OAuth2 + PKCE specification
  const tokenData = new URLSearchParams({
    grant_type: 'authorization_code', // OAuth2 authorization code grant
    client_id: import.meta.env.VITE_KEYCLOAK_CLIENT_ID, // Application identifier
    code: code, // Authorization code from callback
    redirect_uri: 'capacitor://localhost', // Must match the original redirect URI
    code_verifier: codeVerifier // PKCE code verifier for security
  });

  try {
    const response = await fetch(TOKEN_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: tokenData.toString()
    });

    const tokens = (await response.json()) as TokenResponse;

    return tokens;
  } catch (error) {
    console.error('[TokenExchange] Network error during token exchange:', error);
    return { error: 'network_error', error_description: 'Failed to connect to token endpoint' };
  }
};

interface RefreshTokenParams {
  refreshToken: string;
}

/**
 * Refreshes access token using refresh token
 *
 * Uses the refresh_token grant type to obtain a new access token without
 * requiring user re-authentication. This is essential for maintaining
 * user sessions in mobile apps.
 *
 * @param params - Refresh parameters including the refresh token
 * @returns Promise resolving to token response with new access token
 */
export const refreshAccessToken = async (params: RefreshTokenParams): Promise<TokenResponse> => {
  const { refreshToken } = params;

  // Prepare refresh token payload according to OAuth2 specification
  const tokenData = new URLSearchParams({
    grant_type: 'refresh_token', // OAuth2 refresh token grant
    client_id: import.meta.env.VITE_KEYCLOAK_CLIENT_ID, // Application identifier
    refresh_token: refreshToken // The refresh token to exchange
  });

  try {
    const response = await fetch(TOKEN_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: tokenData.toString()
    });

    const tokens = (await response.json()) as TokenResponse;

    return tokens;
  } catch (error) {
    console.error('[TokenRefresh] Network error during token refresh:', error);
    return { error: 'network_error', error_description: 'Failed to connect to token endpoint' };
  }
};
